import {
  addMinutes,
  compareAsc,
  compareDesc,
  format,
  formatDistanceToNowStrict,
  isBefore,
  isDate,
  isThisYear,
  isValid,
  parseISO,
} from 'date-fns';
import { tzOffset } from '@date-fns/tz';
import type { KeysOfType } from '@apparatix/types';

type WeekdayNameLowercase =
  | 'sun'
  | 'mon'
  | 'tue'
  | 'wed'
  | 'thu'
  | 'fri'
  | 'sat'
  | 'sunday'
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday';

export type WeekdayName = WeekdayNameLowercase | Capitalize<WeekdayNameLowercase>;

export const useDateFns = () => {
  const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  /**
   * Formats a raw ISO date into something more readable
   * @param {string | Date} date - Raw input date, expected to be in ISO format
   * @param {string} formatString - Format string to control how date is displayed
   * @see https://date-fns.org/v3.0.0/docs/format for documentation on format strings
   */
  const formatDate = (date: string | Date, formatString: string = 'yyyy-MM-dd', errorMessage = 'Invalid date'): string => {
    if (date instanceof Date) {
      return format(date, formatString);
    }

    try {
      return format(parseISO(date), formatString);
    } catch {
      return errorMessage;
    }
  };

  /**
   * Shows how long ago a date was relative to now. Eg "3 weeks ago", "1 year ago"
   * @param {string | Date} date - Raw input date, expected to be in ISO format
   */
  const formatDistance = (date: string | Date): string => {
    if (date instanceof Date) {
      return `${formatDistanceToNowStrict(date)} ago`;
    }

    try {
      return `${formatDistanceToNowStrict(parseISO(date))} ago`;
    } catch {
      return 'Invalid date';
    }
  };

  /**
   * Checks to see if a given date is in the past
   * @param date The date you want to determine is before now
   * @param exact If true, will compare to the exact minute and second. If false, will only consider the day before now before
   * @returns true if the provided date is before now
   */
  const isInThePast = (date: string | Date, exact: boolean = true): boolean => {
    try {
      const dateInstance = date instanceof Date ? date : parseISO(date);

      const now = new Date();
      if (exact) {
        return isBefore(dateInstance, now);
      }

      now.setHours(0, 0, 0, 0);
      return isBefore(dateInstance, now);
    } catch {
      console.error('Invalid date');
      return false;
    }
  };

  const isInCurrentYear = (date: string | Date): boolean => {
    try {
      const dateInstance = getDateInstance(date);

      return isThisYear(dateInstance);
    } catch {

      return false;
    }
  };

  const getDateInstance = (date: string | Date): Date => date instanceof Date ? date : parseISO(date);

  const parse = (date: string): Date => {
    if (typeof date !== 'string' || date === '') {
      return undefined;
    }

    const parsed = parseISO(date);
    if (Number.isNaN(parsed.valueOf())) {
      return undefined;
    }
    return parsed;
  };

  const isValidDate = (date: string | Date): boolean => isValid(date);

  const areEqual = (a: string | Date, b: string | Date, nullIsEqual: boolean = true): boolean => {
    if (nullIsEqual && a === null && b === null) return true;
    if (!a || !b) return false;
    let aDate: Date, bDate: Date;
    if (typeof a === 'string') {
      aDate = parse(a);
    } else {
      aDate = a;
    }

    if (typeof b === 'string') {
      bDate = parse(b);
    } else {
      bDate = b;
    }

    return compareAsc(aDate, bDate) === 0;
  };

  /*
   * Date Formatting - https://date-fns.org/v3.0.0/docs/format
   */
  const getTimestampFormat = (): string => 'yyyyMMddHHmmss';

  /**
   * Creates a sorter function that can be passed to Array.sort
   * @param dateField The field of the input object that contains the date you wish to sort by
   * @param order
   * @returns a sorter callback function
   */
  const createArrayDateSorter = <T extends object>(dateField: KeysOfType<T, string | Date>, order: 'ASC' | 'DESC' = 'ASC') => (a: T, b: T): number => {
    if (!(dateField in a) || !(dateField in b)) return NaN;

    const aValue = a[dateField];
    const bValue = b[dateField];

    const aDate = aValue instanceof Date ? aValue : parse(aValue as string);
    const bDate = bValue instanceof Date ? bValue : parse(bValue as string);

    if (order === 'ASC') {
      return compareAsc(aDate, bDate);
    }

    return compareDesc(aDate, bDate);
  };

  const formatTimeFromDate = (date: Date, hourFormat: '12hr' | '24hr' = '12hr') => hourFormat === '12hr'
    ? format(date, 'h:mmaaa')
    : format(date, 'HH:mm');

  /**
   * @description Test whether a given date occurs on the specified day of the week
   * Algorithm adapted from https://en.wikipedia.org/wiki/Zeller%27s_congruence
   */
  const isDayOfWeek = (day: WeekdayName, date: Date) => {
    if (!isDate(date)) {
      throw new Error('`date` is not a valid date');
    }

    const year = date.getFullYear();

    // JS returns 0 indexed months, but Zeller expects 1 indexed months
    const month = date.getMonth() + 1;

    const adjustedYear = month < 3 ? year - 1 : year;
    const q = date.getDate();
    const K = adjustedYear % 100;
    const J = Math.floor(adjustedYear / 100);

    const m = month === 1
      ? 13
      : month === 2
        ? 14
        : month;

    const h = (
      q +
      Math.floor((13 * (m + 1)) / 5) +
      K +
      Math.floor(K / 4) +
      Math.floor(J / 4) -
      (2 * J)
    ) % 7;

    // Zeller's returns Saturday = 1, this converts to ISO Monday = 1
    const isoDay = ((h + 5) % 7) + 1;

    const dayMap: Record<WeekdayNameLowercase, number> = {
      sun: 7,
      mon: 1,
      tue: 2,
      wed: 3,
      thu: 4,
      fri: 5,
      sat: 6,
      sunday: 7,
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
    };

    return (day?.toLowerCase() in dayMap) && dayMap[day.toLowerCase()] === isoDay;
  };

  /**
   * @description Adjust a date to the specified time zone
   * @param date
   * @param tz timezone to adjust to. Defaults to the browser's local tz
   */
  const adjustUtcToLocal = (date: Date, tz = localTz) => addMinutes(date, tzOffset(tz, date));

  return {
    adjustUtcToLocal,
    areEqual,
    createArrayDateSorter,
    formatDate,
    formatDistance,
    formatTimeFromDate,
    getTimestampFormat,
    isDayOfWeek,
    isInCurrentYear,
    isInThePast,
    isValidDate,
    parse,
  };
};
